import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    inject,
    OnDestroy,
    OnInit,
    Type
} from '@angular/core';
import { ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { auditTime, debounceTime, filter, first, map, withLatestFrom } from 'rxjs/operators';
import { getHostPropertyClass } from '../../dynamic-form.constants';
import { DynamicFormHelper } from '../../dynamic-form.helper';
import { SsnFieldConfig } from '@trade-platform/form-fields';
import { Field, FieldEvent } from '../field.interface';
import {
    createSeparatorMask,
    objectHasValue,
    stringIsNotEmpty,
    unMaskSeparatorValue
} from '@trade-platform/ui-utils';
import { DynamicFormStore } from '../../dynamic-form-store';
import { getDataPathByFieldConfig } from '../../dynamic-form-store/utils';
import {
    DynamicFormControlSetDirtyAction,
    DynamicFormSetDataAction
} from '../../dynamic-form-store/actions';
import { handleServerValdationErrors } from '../../dynamic-form.utils';
import {
    FieldDecoration,
    FieldDecorationDirective
} from '../field-decoration/field-decoration.directive';
import { FieldDecorationMap } from '../field-decoration/field-decoration-map';
import { AixFormErrorsPipe } from '../../../pipes/form-errors';
import { AixDynamicNextPendingFieldDirective } from '../../directives/dynamic-next-pending-field';
import { AixDataTestingDirective, AixSsnComponent } from '@trade-platform/ui-components';

@Component({
    selector: 'aix-dynamic-ssn',
    styleUrls: ['./ssn.component.scss'],
    templateUrl: './ssn.component.html',
    standalone: true,
    imports: [
        AixSsnComponent,
        AixDynamicNextPendingFieldDirective,
        FieldDecorationDirective,
        AixDataTestingDirective,
        AixFormErrorsPipe
    ]
})
export class AixDynamicSsnComponent implements Field, OnInit, OnDestroy {
    private helper = inject(DynamicFormHelper);
    private store = inject<Store<any>>(Store);
    private formStore = inject(DynamicFormStore);
    private elemRef = inject(ElementRef);
    private cd = inject(ChangeDetectorRef);

    // Static
    static HOST_CLASS = 'aix-flex-grid__col';

    // Decorators
    @HostBinding('class')
    classNames = AixDynamicSsnComponent.HOST_CLASS;

    @HostBinding('attr.aix-control')
    aixControl: string;

    // Store Data
    templateData = {
        fieldIsDisabled: false,
        fieldIsRequired: false,
        fieldIsDirty: false,
        ctrlIsInvalid: false,
        ctrlHasRequiredError: false,
        canDisplayErrors: false,
        ctrlErrors: null as ValidationErrors | null
    };
    userInputEmitter$ = new Subject<string>();
    debouncer = new Subject<string>();

    value: string;

    // Other
    config: SsnFieldConfig;
    private subscriptions: Subscription[] = [];
    fieldDecoration: Type<FieldDecoration>;
    fieldDecorationParams: FieldDecoration['params'];
    fieldDecorationHostObservables: FieldDecoration['hostObservables'];

    private readonly MAX_LENGTH = 9;
    private mask = createSeparatorMask([3, 5], this.MAX_LENGTH, '-');
    private isTyping = false;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.cd.detach();

        const formUID = this.formStore.formUID;

        // User input
        this.subscriptions.push(
            this.userInputEmitter$.subscribe(value => {
                this.store.dispatch(
                    new DynamicFormSetDataAction(
                        formUID,
                        getDataPathByFieldConfig(this.config),
                        value
                    )
                );
            }),
            this.debouncer
                .pipe(debounceTime(this.helper.INPUT_DEBOUNCE))
                .subscribe(value => this.userInputEmitter$.next(value))
        );
    }

    ngOnInit() {
        this.classNames = this.calculateClassNames();
        this.initializeFieldDecorations();

        const refId = this.config.refId as string;

        // Control initialization
        this.aixControl = refId;
        this.formStore.addControl(this.config);

        // Store Control Observables
        const ctrlStore$ = this.formStore.getControlStoreByRefId(refId);
        const valueToRender$ = this.formStore
            .getDataStore<string>(getDataPathByFieldConfig(this.config))
            .pipe(map(value => this.mask(unMaskSeparatorValue(value) as string)));

        this.subscriptions.push(
            ctrlStore$
                .pipe(auditTime(5), withLatestFrom(valueToRender$))
                .subscribe(([ctrl, valueToRender]) => {
                    if (!this.isTyping) {
                        // update input by calculated expressions, patch values or data loads from store actions
                        this.valueToSend(valueToRender);
                    }

                    if (ctrl.configUpdateByRelation) {
                        this.config = {
                            ...(ctrl.fieldConfig as SsnFieldConfig)
                        };
                    }

                    // fieldIsDisabled
                    this.templateData.fieldIsDisabled =
                        ctrl.disabledByRelation || ctrl.fieldConfig.disabled === true;

                    // fieldIsRequired
                    const hasRequiredValidator = (
                        ctrl.fieldConfig.validation as ValidatorFn[]
                    ).some(val => val === Validators.required);
                    this.templateData.fieldIsRequired =
                        ctrl.requiredByRelation || hasRequiredValidator;

                    // fieldIsDirty
                    this.templateData.fieldIsDirty = ctrl.isDirty;

                    // ctrlErrors
                    this.templateData.ctrlErrors = ctrl.validation;

                    // ctrlHasRequiredError
                    this.templateData.ctrlHasRequiredError =
                        ctrl.validation && ctrl.validation['required'];

                    // ctrlIsInvalid
                    this.templateData.ctrlIsInvalid = ctrl.validation !== null;

                    // canDisplayErrors
                    const hasNonRequiredErrors =
                        !!ctrl.validation &&
                        Object.keys(ctrl.validation).some(key => key !== 'required');
                    this.templateData.canDisplayErrors =
                        (ctrl.validation !== null && ctrl.isDirty) || hasNonRequiredErrors;

                    // Server Validation errors
                    handleServerValdationErrors(ctrl, evt =>
                        (this.elemRef.nativeElement as HTMLElement).dispatchEvent(evt)
                    );

                    this.cd.detectChanges();

                    this.isTyping = false;
                })
        );

        // Notifications
        this.subscriptions.push(
            combineLatest([
                valueToRender$.pipe(
                    first(),
                    map(rawValue => this.valueToSend(rawValue))
                ),
                this.userInputEmitter$
            ])
                .pipe(
                    filter(
                        ([defaultValue, userInput]) =>
                            stringIsNotEmpty(defaultValue) && defaultValue !== userInput
                    )
                )
                .subscribe(_ => {
                    const aixEvt = new CustomEvent(FieldEvent.CHANGE, {
                        detail: this.config,
                        bubbles: true,
                        cancelable: true
                    });
                    this.elemRef.nativeElement.dispatchEvent(aixEvt);
                })
        );

        // Store Actions
        this.subscriptions.push(
            this.userInputEmitter$
                .pipe(
                    debounceTime(100),
                    filter(value => /\d\d\d\d\d\d\d\d\d/.test(value))
                )
                .subscribe(value => {
                    this.helper.dispatchStoreActions(this.store, this.config, value);
                })
        );

        // On Load Store Actions
        this.subscriptions.push(
            valueToRender$
                .pipe(
                    first(),
                    map(rawValue => this.valueToSend(rawValue))
                )
                .subscribe(value =>
                    this.helper.dispatchOnLoadStoreActions(this.store, this.config, value)
                )
        );

        this.cd.detectChanges();
    }

    valueChange(value: string) {
        this.isTyping = true;
        this.value = value;
        this.debouncer.next(value);
    }

    setDirty() {
        this.store.dispatch(
            new DynamicFormControlSetDirtyAction(
                this.formStore.formUID,
                this.config.refId as string
            )
        );
    }

    onFocus() {
        if (this.fieldDecorationHostObservables) {
            this.fieldDecorationHostObservables.hasFocus.next(true);
        }
    }

    onBlur() {
        if (this.fieldDecorationHostObservables) {
            this.fieldDecorationHostObservables.hasFocus.next(false);
        }
    }

    initializeFieldDecorations() {
        if (objectHasValue(this.config.decoration)) {
            this.fieldDecorationParams = this.config.decoration.params;
            this.fieldDecorationHostObservables = {
                hasFocus: new BehaviorSubject<boolean>(false)
            };
            this.fieldDecoration = FieldDecorationMap.get(this.config.decoration.ref);
        }
    }

    valueToSend(rawValue: string) {
        const unmaskedValue = unMaskSeparatorValue(rawValue ?? '', this.MAX_LENGTH);
        this.value = unmaskedValue && unmaskedValue.length ? unmaskedValue : '';
        return this.value;
    }

    calculateClassNames() {
        let classNames = this.config.classNames
            ? [
                  AixDynamicSsnComponent.HOST_CLASS,
                  ...this.helper.parseHostProperties(this.config.classNames.host)
              ].join(' ')
            : AixDynamicSsnComponent.HOST_CLASS;
        classNames = this.config.hidden
            ? classNames.concat(` ${getHostPropertyClass().hidden}`)
            : classNames;
        return classNames;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.formStore.removeControl(this.config);
    }
}
